import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  WalletIcon,
  DevicePhoneMobileIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  UserIcon,
  ArrowsRightLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { walletApi, WalletTxn } from '@/api/walletApi'
import { getParentDashboard, ChildInfo } from '@/api/dashboardApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { usePaymentWebSocket, PaymentStatus } from '@/hooks/usePaymentWebSocket'

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type TopUpMethod = 'mpesa' | 'wallet'
type TransactionFilter = 'ALL' | 'CREDIT' | 'DEBIT'
type ViewMode = 'topup' | 'transactions'

const quickAmounts = [500, 1000, 2000, 5000, 10000]

export default function StudentWalletTopUpPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  const preselectedStudentId = searchParams.get('studentId')

  const [selectedStudentId, setSelectedStudentId] = useState<string>(preselectedStudentId || '')
  const [topUpMethod, setTopUpMethod] = useState<TopUpMethod>('mpesa')
  const [amount, setAmount] = useState<number>(0)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [showPaymentStatus, setShowPaymentStatus] = useState(false)
  const [paymentRef, setPaymentRef] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('topup')
  const [transactionFilter, setTransactionFilter] = useState<TransactionFilter>('ALL')
  const [currentPage, setCurrentPage] = useState(1)

  // WebSocket hook for real-time M-Pesa payment status
  const {
    status: wsStatus,
    message: wsMessage,
    isConnected,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    notification,
  } = usePaymentWebSocket({
    onComplete: (notif) => {
      toast.success(notif.message || 'Top-up completed successfully!')
      queryClient.invalidateQueries({ queryKey: ['studentWallet', selectedStudentId] })
      queryClient.invalidateQueries({ queryKey: ['studentWalletTransactions', selectedStudentId] })
      // Auto close after showing success
      setTimeout(() => {
        setShowPaymentStatus(false)
        setPaymentRef('')
        setAmount(0)
        setPhoneNumber('')
      }, 3000)
    },
    onError: (notif) => {
      toast.error(notif.message || 'Top-up failed')
    },
  })

  const closePaymentStatus = () => {
    disconnectWebSocket()
    setShowPaymentStatus(false)
    setPaymentRef('')
  }

  // Get status display info
  const getStatusDisplay = (status: PaymentStatus) => {
    switch (status) {
      case 'PENDING':
        return {
          icon: <ClockIcon className="h-16 w-16 text-yellow-500" />,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          spinnerColor: 'border-yellow-500',
        }
      case 'PROCESSING':
        return {
          icon: <DevicePhoneMobileIcon className="h-16 w-16 text-blue-500" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          spinnerColor: 'border-blue-500',
        }
      case 'COMPLETED':
        return {
          icon: <CheckCircleIcon className="h-16 w-16 text-green-500" />,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          spinnerColor: 'border-green-500',
        }
      case 'FAILED':
      case 'CANCELLED':
        return {
          icon: <XCircleIcon className="h-16 w-16 text-red-500" />,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          spinnerColor: 'border-red-500',
        }
      case 'TIMEOUT':
        return {
          icon: <ExclamationTriangleIcon className="h-16 w-16 text-orange-500" />,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          spinnerColor: 'border-orange-500',
        }
      default:
        return {
          icon: <ClockIcon className="h-16 w-16 text-gray-500" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          spinnerColor: 'border-gray-500',
        }
    }
  }

  // Get parent dashboard data (includes children list)
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['parentDashboard'],
    queryFn: () => getParentDashboard(),
  })

  // Get parent's own wallet
  const { data: parentWalletData, isLoading: parentWalletLoading } = useQuery({
    queryKey: ['myWallet'],
    queryFn: () => walletApi.getMyWallet(),
  })

  // Get selected student's wallet
  const { data: studentWalletData, isLoading: studentWalletLoading } = useQuery({
    queryKey: ['studentWallet', selectedStudentId],
    queryFn: () => walletApi.getStudentWallet(selectedStudentId),
    enabled: !!selectedStudentId,
  })

  // Get student wallet transactions with filtering
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ['studentWalletTransactions', selectedStudentId, transactionFilter, currentPage],
    queryFn: () =>
      walletApi.getStudentWalletTransactions(selectedStudentId, {
        page: currentPage,
        limit: 10,
        type: transactionFilter === 'ALL' ? undefined : transactionFilter,
      }),
    enabled: !!selectedStudentId && viewMode === 'transactions',
  })

  // M-Pesa top-up mutation
  const mpesaTopUpMutation = useMutation({
    mutationFn: walletApi.topUpStudentWallet,
    onSuccess: (data) => {
      if (data.success) {
        const ref = data.data?.paymentRef || ''
        setPaymentRef(ref)
        setShowPaymentStatus(true)
        // Connect to WebSocket for real-time status updates
        if (ref) {
          connectWebSocket(ref)
        }
        toast.success(data.message || 'Payment request sent! Check your phone.')
        queryClient.invalidateQueries({ queryKey: ['studentWalletTransactions', selectedStudentId] })
      } else {
        toast.error(data.message || 'Failed to initiate payment')
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to initiate payment')
    },
  })

  // Wallet transfer mutation
  const walletTransferMutation = useMutation({
    mutationFn: walletApi.transferToStudent,
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || `Successfully transferred ${formatCurrency(data.data.amount)} to ${data.data.studentWallet.studentName}'s wallet`)
        queryClient.invalidateQueries({ queryKey: ['studentWallet', selectedStudentId] })
        queryClient.invalidateQueries({ queryKey: ['studentWalletTransactions', selectedStudentId] })
        queryClient.invalidateQueries({ queryKey: ['myWallet'] })
        setTimeout(() => {
          setAmount(0)
        }, 2000)
      } else {
        toast.error(data.message || 'Transfer failed')
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Transfer failed')
    },
  })

  // Pre-select student if passed in URL
  useEffect(() => {
    if (preselectedStudentId) {
      setSelectedStudentId(preselectedStudentId)
    }
  }, [preselectedStudentId])

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [transactionFilter])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedStudentId) {
      toast.error('Please select a student')
      return
    }

    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (topUpMethod === 'mpesa') {
      if (!phoneNumber || phoneNumber.length < 9) {
        toast.error('Please enter a valid phone number')
        return
      }
      mpesaTopUpMutation.mutate({
        studentId: selectedStudentId,
        amount,
        phoneNumber,
        description: `Top-up for ${studentWalletData?.data?.student?.firstName || 'student'}'s wallet`,
      })
    } else {
      // Wallet transfer
      const parentBalance = parentWalletData?.data?.balance || 0
      if (parentBalance < amount) {
        toast.error(`Insufficient wallet balance. You have ${formatCurrency(parentBalance)}`)
        return
      }
      walletTransferMutation.mutate({
        studentId: selectedStudentId,
        amount,
        description: `Transfer to ${studentWalletData?.data?.student?.firstName || 'student'}'s wallet`,
      })
    }
  }

  const children = dashboardData?.data?.children || []
  const parentWallet = parentWalletData?.data
  const studentWallet = studentWalletData?.data?.wallet
  const student = studentWalletData?.data?.student
  const summary = studentWalletData?.data?.summary
  const recentTransactions = studentWalletData?.data?.recentTransactions || []
  const transactions = transactionsData?.data?.transactions || []
  const pagination = transactionsData?.data?.pagination

  const isLoading = dashboardLoading || parentWalletLoading
  const isPending = mpesaTopUpMutation.isPending || walletTransferMutation.isPending

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/parent/wallet')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Student Wallet</h1>
            <p className="text-gray-600">Manage your child's wallet and view transactions</p>
          </div>
        </div>
      </div>

      {/* Student Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Student</h2>

        {children.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UserIcon className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>No students found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {children.map((child: ChildInfo) => (
              <button
                key={child.id}
                type="button"
                onClick={() => {
                  setSelectedStudentId(child.id)
                  setViewMode('topup')
                }}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  selectedStudentId === child.id
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedStudentId === child.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <UserIcon className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">
                    {child.firstName} {child.lastName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {child.grade && `Grade ${child.grade}`}
                    {child.stream && ` - ${child.stream}`}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Student Wallet Info & Summary */}
      {selectedStudentId && (
        <>
          {/* Wallet Balance Card */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
            {studentWalletLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    <WalletIcon className="w-7 h-7" />
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm">
                      {student?.firstName} {student?.lastName}'s Wallet Balance
                    </p>
                    <p className="text-3xl font-bold">
                      {formatCurrency(studentWallet?.balance || 0, studentWallet?.currency)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="text-center px-4 py-2 bg-white/10 rounded-lg">
                    <p className="text-blue-100 text-xs">Your Wallet</p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(parentWallet?.balance || 0, parentWallet?.currency)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <ArrowUpIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Credits</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(summary.totalCredits)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <ArrowDownIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Debits</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(summary.totalDebits)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Credit Count</p>
                    <p className="text-lg font-bold text-gray-900">{summary.creditCount}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <BanknotesIcon className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Debit Count</p>
                    <p className="text-lg font-bold text-gray-900">{summary.debitCount}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* View Mode Tabs */}
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setViewMode('topup')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                viewMode === 'topup'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Top Up Wallet
            </button>
            <button
              onClick={() => setViewMode('transactions')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                viewMode === 'transactions'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Transaction History
            </button>
          </div>

          {/* Top-up Form */}
          {viewMode === 'topup' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose Top-up Method</h2>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <button
                      type="button"
                      onClick={() => setTopUpMethod('mpesa')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        topUpMethod === 'mpesa'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <DevicePhoneMobileIcon className={`w-8 h-8 ${topUpMethod === 'mpesa' ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className={`font-medium ${topUpMethod === 'mpesa' ? 'text-green-600' : 'text-gray-700'}`}>
                        M-Pesa
                      </span>
                      <span className="text-xs text-gray-500">Pay via STK Push</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setTopUpMethod('wallet')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        topUpMethod === 'wallet'
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <ArrowsRightLeftIcon className={`w-8 h-8 ${topUpMethod === 'wallet' ? 'text-primary-600' : 'text-gray-400'}`} />
                      <span className={`font-medium ${topUpMethod === 'wallet' ? 'text-primary-600' : 'text-gray-700'}`}>
                        Wallet Transfer
                      </span>
                      <span className="text-xs text-gray-500">From your wallet</span>
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Quick Amount Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Select Amount
                      </label>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {quickAmounts.map((quickAmount) => (
                          <button
                            key={quickAmount}
                            type="button"
                            onClick={() => setAmount(quickAmount)}
                            className={`px-4 py-3 text-sm font-medium rounded-lg border transition-colors ${
                              amount === quickAmount
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {formatCurrency(quickAmount)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Or enter custom amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                          KES
                        </span>
                        <input
                          type="number"
                          value={amount || ''}
                          onChange={(e) => setAmount(Number(e.target.value))}
                          placeholder="0"
                          min={1}
                          max={150000}
                          className="w-full pl-14 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
                        />
                      </div>
                    </div>

                    {/* Phone Number (only for M-Pesa) */}
                    {topUpMethod === 'mpesa' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          M-Pesa Phone Number
                        </label>
                        <div className="relative">
                          <DevicePhoneMobileIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="0712 345 678"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Enter the phone number registered with M-Pesa
                        </p>
                      </div>
                    )}

                    {/* Wallet Balance Warning (only for wallet transfer) */}
                    {topUpMethod === 'wallet' && amount > 0 && (parentWallet?.balance || 0) < amount && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">
                          Insufficient wallet balance. You have {formatCurrency(parentWallet?.balance || 0)} but need {formatCurrency(amount)}.
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate('/parent/wallet/topup')}
                          className="mt-2 text-sm font-medium text-red-700 hover:text-red-800 underline"
                        >
                          Top up your wallet first
                        </button>
                      </div>
                    )}

                    {/* Summary */}
                    {amount > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Amount to top up</span>
                          <span className="text-xl font-bold text-gray-900">
                            {formatCurrency(amount)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-2 text-sm">
                          <span className="text-gray-500">
                            {student?.firstName}'s new balance will be
                          </span>
                          <span className="font-medium text-green-600">
                            {formatCurrency((studentWallet?.balance || 0) + amount)}
                          </span>
                        </div>
                        {topUpMethod === 'wallet' && (
                          <div className="flex justify-between items-center mt-2 text-sm border-t border-gray-200 pt-2">
                            <span className="text-gray-500">Your wallet balance after</span>
                            <span className="font-medium text-gray-600">
                              {formatCurrency((parentWallet?.balance || 0) - amount)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isPending || amount <= 0 || !selectedStudentId || (topUpMethod === 'mpesa' && !phoneNumber) || (topUpMethod === 'wallet' && (parentWallet?.balance || 0) < amount)}
                      className={`w-full flex items-center justify-center gap-2 px-6 py-4 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                        topUpMethod === 'mpesa'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-primary-600 hover:bg-primary-700'
                      }`}
                    >
                      {isPending ? (
                        <>
                          <LoadingSpinner size="sm" />
                          {topUpMethod === 'mpesa' ? 'Sending payment request...' : 'Transferring...'}
                        </>
                      ) : (
                        <>
                          {topUpMethod === 'mpesa' ? (
                            <>
                              <BanknotesIcon className="w-5 h-5" />
                              Pay with M-Pesa
                            </>
                          ) : (
                            <>
                              <ArrowsRightLeftIcon className="w-5 h-5" />
                              Transfer from Wallet
                            </>
                          )}
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {/* Recent Transactions Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">Recent Transactions</h3>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                    {studentWalletLoading ? (
                      <div className="p-4 flex justify-center">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : recentTransactions.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <BanknotesIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm">No transactions yet</p>
                      </div>
                    ) : (
                      recentTransactions.map((txn: WalletTxn) => (
                        <div key={txn._id} className="p-4 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className={`font-medium ${txn.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>
                                {txn.type === 'CREDIT' ? '+' : '-'} {formatCurrency(txn.amount, txn.currency)}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                {txn.description}
                              </p>
                            </div>
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                txn.type === 'CREDIT'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {txn.type}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            {formatDate(txn.createdAt)}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                  {recentTransactions.length > 0 && (
                    <div className="p-3 border-t border-gray-200">
                      <button
                        onClick={() => setViewMode('transactions')}
                        className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View All Transactions
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Transaction History View */}
          {viewMode === 'transactions' && (
            <div className="bg-white rounded-xl border border-gray-200">
              {/* Filter Bar */}
              <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <FunnelIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Filter:</span>
                  <div className="flex gap-1">
                    {(['ALL', 'CREDIT', 'DEBIT'] as TransactionFilter[]).map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setTransactionFilter(filter)}
                        className={`px-3 py-1 text-sm rounded-full transition-colors ${
                          transactionFilter === filter
                            ? filter === 'CREDIT'
                              ? 'bg-green-100 text-green-800'
                              : filter === 'DEBIT'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-primary-100 text-primary-800'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {filter === 'ALL' ? 'All' : filter === 'CREDIT' ? 'Credits' : 'Debits'}
                      </button>
                    ))}
                  </div>
                </div>
                {pagination && (
                  <p className="text-sm text-gray-500">
                    Showing {transactions.length} of {pagination.total} transactions
                  </p>
                )}
              </div>

              {/* Transactions List */}
              <div className="divide-y divide-gray-100">
                {transactionsLoading ? (
                  <div className="p-8 flex justify-center">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <BanknotesIcon className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    <p>No transactions found</p>
                  </div>
                ) : (
                  transactions.map((txn: WalletTxn) => (
                    <div key={txn._id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          txn.type === 'CREDIT' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {txn.type === 'CREDIT' ? (
                            <ArrowUpIcon className="w-5 h-5 text-green-600" />
                          ) : (
                            <ArrowDownIcon className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{txn.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              txn.type === 'CREDIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {txn.category.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-gray-400">{formatDate(txn.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${txn.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.type === 'CREDIT' ? '+' : '-'} {formatCurrency(txn.amount, txn.currency)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Balance: {formatCurrency(txn.balanceAfter, txn.currency)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {pagination && pagination.pages > 1 && (
                <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(pagination.pages, prev + 1))}
                    disabled={currentPage === pagination.pages}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Payment Status Overlay for M-Pesa */}
      {showPaymentStatus && paymentRef && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
            {/* Status Header */}
            <div className={`px-6 py-4 ${getStatusDisplay(wsStatus).bgColor}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Top-up Status</h3>
                {isConnected && (
                  <span className="flex items-center text-xs text-green-600">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                    Live
                  </span>
                )}
              </div>
            </div>

            {/* Status Body */}
            <div className="px-6 py-8">
              <div className="text-center">
                {/* Spinner for pending/processing states */}
                {(wsStatus === 'PENDING' || wsStatus === 'PROCESSING' || wsStatus === 'IDLE') && (
                  <div className="relative mx-auto w-20 h-20 mb-6">
                    <div className={`absolute inset-0 border-4 ${getStatusDisplay(wsStatus).spinnerColor} border-t-transparent rounded-full animate-spin`}></div>
                    <div className="absolute inset-2 flex items-center justify-center">
                      {wsStatus === 'PROCESSING' ? (
                        <DevicePhoneMobileIcon className="h-8 w-8 text-blue-500" />
                      ) : (
                        <ClockIcon className="h-8 w-8 text-yellow-500" />
                      )}
                    </div>
                  </div>
                )}

                {/* Icon for final states */}
                {(wsStatus === 'COMPLETED' || wsStatus === 'FAILED' || wsStatus === 'CANCELLED' || wsStatus === 'TIMEOUT') && (
                  <div className="mb-6">
                    {getStatusDisplay(wsStatus).icon}
                  </div>
                )}

                {/* Status Text */}
                <h4 className={`text-xl font-semibold mb-2 ${getStatusDisplay(wsStatus).color}`}>
                  {wsStatus === 'IDLE' && 'Connecting...'}
                  {wsStatus === 'PENDING' && 'Waiting for Payment'}
                  {wsStatus === 'PROCESSING' && 'Check Your Phone'}
                  {wsStatus === 'COMPLETED' && 'Top-up Successful!'}
                  {wsStatus === 'FAILED' && 'Top-up Failed'}
                  {wsStatus === 'CANCELLED' && 'Top-up Cancelled'}
                  {wsStatus === 'TIMEOUT' && 'Top-up Timed Out'}
                </h4>

                <p className="text-sm text-gray-600 mb-4">
                  {wsMessage || 'Please wait while we process your top-up...'}
                </p>

                {/* Transaction details for completed payments */}
                {wsStatus === 'COMPLETED' && notification && (
                  <div className="bg-green-50 rounded-lg p-4 mt-4 text-left">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Amount Added:</span>
                      <span className="font-semibold text-green-600">
                        {notification.amount ? formatCurrency(notification.amount) : '-'}
                      </span>
                    </div>
                    {notification.transactionRef && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">M-Pesa Receipt:</span>
                        <span className="font-mono text-gray-900">{notification.transactionRef}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment reference */}
                <p className="text-xs text-gray-400 mt-4">
                  Reference: {paymentRef}
                </p>
              </div>
            </div>

            {/* Status Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-center">
              {(wsStatus === 'COMPLETED' || wsStatus === 'FAILED' || wsStatus === 'CANCELLED' || wsStatus === 'TIMEOUT') ? (
                <button
                  onClick={closePaymentStatus}
                  className={`px-6 py-2 rounded-lg text-white ${
                    wsStatus === 'COMPLETED' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
                  }`}
                >
                  {wsStatus === 'COMPLETED' ? 'Done' : 'Close'}
                </button>
              ) : (
                <button
                  onClick={closePaymentStatus}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
